import React, { useEffect, useState } from 'react';
import io from 'socket.io-client';

/**
 * Mounted once, globally. While a user is logged in, keeps a socket
 * registered so that if a second device tries to log into the same
 * account, this device displays a confirmation code in real time —
 * the new device has to be given that code to complete its login,
 * instead of a bare accept/deny tap (or silently losing the session).
 * Not sharing the code (or letting it expire) is the implicit "deny".
 */
const AuthSessionListener = () => {
  const [challenge, setChallenge] = useState(null); // { requestId, code, expiresInSec }
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const userId = localStorage.getItem('id');
    const token = localStorage.getItem('token');
    if (!userId || !token) return;

    const BACKEND_URL = process.env.REACT_APP_BASE_URL || 'https://geo.geomedium.com';
    const socket = io(BACKEND_URL, {
      path: '/api/socket',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
      withCredentials: true,
    });

    socket.on('connect', () => socket.emit('register-session', { userId }));
    socket.on('login-code-challenge', (data) => {
      if (data?.requestId && data?.code) {
        setChallenge(data);
        setSecondsLeft(data.expiresInSec || 300);
        setDismissed(false); // a fresh challenge should always show, even if a previous one was closed
      }
    });

    return () => socket.disconnect();
  }, []);

  useEffect(() => {
    if (!challenge || secondsLeft <= 0) return;
    const timer = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          setChallenge(null);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [challenge, secondsLeft]);

  // dismissed hides the card only — the countdown effect above is keyed on
  // challenge/secondsLeft, not on this, so it keeps ticking in the background
  // and the code still expires on schedule even while closed.
  if (!challenge || dismissed) return null;

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 999999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.6)',
      }}
    >
      <div
        style={{
          position: 'relative',
          background: '#1e2530', color: '#fff', borderRadius: 10,
          padding: '24px 26px', maxWidth: 360, width: '90%',
          boxShadow: '0 10px 40px rgba(0,0,0,0.4)', textAlign: 'center',
        }}
      >
        <button
          type="button"
          onClick={() => setDismissed(true)}
          title="Close"
          style={{
            position: 'absolute', top: 10, right: 10,
            background: 'none', border: 'none', color: '#fff', opacity: 0.7,
            fontSize: 16, cursor: 'pointer', lineHeight: 1,
          }}
        >
          <i className="fa-solid fa-xmark" />
        </button>
        <i className="fa-solid fa-shield-halved" style={{ fontSize: 28, color: '#f39c12', marginBottom: 10 }} />
        <h5 style={{ margin: '8px 0 6px' }}>Another device is trying to log in</h5>
        <p style={{ opacity: 0.8, fontSize: 14, marginBottom: 14 }}>
          If this was you, enter this code on the other device to continue.
          If it wasn't, ignore this — it expires on its own.
        </p>
        <div
          style={{
            fontSize: 32, fontWeight: 700, letterSpacing: 6,
            background: '#111623', borderRadius: 8, padding: '10px 0', marginBottom: 10,
          }}
        >
          {challenge.code}
        </div>
        <p style={{ opacity: 0.6, fontSize: 12, margin: 0 }}>Expires in {mm}:{ss}</p>
      </div>
    </div>
  );
};

export default AuthSessionListener;
